import { useMutation, useQuery }                from '@apollo/client'
import { XCircleIcon }                          from '@heroicons/react/20/solid'
import { ArrowUturnLeftIcon }                   from '@heroicons/react/24/solid'
import { UploadCloud }                          from 'lucide-react'
import { useEffect, useState }                  from 'react'
import Dropzone                                 from 'react-dropzone'
import { useDispatch, useSelector }             from 'react-redux'
import { useNavigate }                          from 'react-router-dom'
import { Button, TextField }                    from '../../../components'
import { LoadingEllipsis }                      from '../../../components/Loaders'
import { Image }                                from '../../../components/Image'
import { setToasts, showToaster }               from '../../../features/toaster/toasterSlice'
import MainLayout                               from '../../../layouts/MainLayout'
import { appCdnUrl, assetFilePath, uploadFile } from '../../../utilities'
import {
  GET_SETTINGS_BY_HANDLE,
  UPDATE_SETTINGS,
  CREATE_SETTINGS_ATTACHMENT,
  DELETE_SETTINGS_ATTACHMENT_BY_URL
}                                               from '../../../graphql/settings'

type Settings = {
  id: string
  name: string
  handle: string
  config: {
    title?: string
    description?: string
    ctaTitle?: string
    link?: string
    imageUrl?: string
    imageWideDesktopUrl?: string
    imageDesktopUrl?: string
    imageTabletUrl?: string
    imageMobileUrl?: string
    [ k: string ]: any
  }
}

type BannerImageField = 'imageWideDesktopUrl' | 'imageDesktopUrl' | 'imageTabletUrl' | 'imageMobileUrl'

type BannerUpload = {
  field: BannerImageField
  title: string
  size: string
}

type FormData = {
  name: string
  handle: string
  config: Settings['config']
}

const bannerUploads: BannerUpload[] = [
  {
    field:    'imageWideDesktopUrl',
    title:    'Wide desktop banner',
    size:     'Recommended size: 2352 x 620px'
  },
  {
    field:    'imageDesktopUrl',
    title:    'Desktop banner',
    size:     'Recommended size: 1920 x 620px'
  },
  {
    field:    'imageTabletUrl',
    title:    'Tablet banner',
    size:     'Recommended size: 1200 x 520px'
  },
  {
    field:    'imageMobileUrl',
    title:    'Mobile banner',
    size:     'Recommended size: 768 x 640px'
  }
]

function FilePreview( { file, onRemove }: { file: any, onRemove: () => void } ): JSX.Element {
  const [ previewUrl, setPreviewUrl ] = useState( '' )

  useEffect( () => {
    if ( !file || !file.type.startsWith( 'image/' ) ) return

    const objectUrl = URL.createObjectURL( file )
    setPreviewUrl( objectUrl )

    return () => URL.revokeObjectURL( objectUrl )
  }, [ file ] )

  return (
    <div className="flex flex-col w-16 h-auto mr-2 relative rounded-md">
      <button
        title="Remove"
        className="absolute -right-1 -top-1 bg-white shadow rounded-full"
        onClick={ onRemove }
      >
        <XCircleIcon className="w-4 h-4" />
      </button>

      <Image
        klass="w-16 h-16 object-cover rounded"
        src={ previewUrl }
        defaultSrc={ assetFilePath( file.type.split( '/' ).pop() ) }
      />

      <small className="text-xs truncate w-16">{ file.name }</small>
    </div>
  )
}

export default function SettingsDashboardBanner(): JSX.Element {
  const navigate                          = useNavigate()
  const dispatch                          = useDispatch()
  const currentUser: any                  = useSelector( ( state: any ) => state.currentUser.user )
  const currentTenant: any                = useSelector( ( state: any ): any => state.currentTenant.tenant )
  const [ settings, setSettings ]         = useState<Settings | null>( null )
  const [ formData, setFormData ]         = useState<FormData>( {
                                                                  name:   '',
                                                                  handle: '',
                                                                  config: {}
                                                                } )
  const [ errors, setErrors ]             = useState<Record<string, boolean>>( {} )
  const [ uploading, setUploading ]       = useState( false )
  const [ filesToUpload, setFilesToUpload ] = useState<Partial<Record<BannerImageField, any>>>( {} )

  const dispatchMessage = ( type: 'success' | 'error' | 'warning', message: string ) => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  const { loading, refetch } = useQuery( GET_SETTINGS_BY_HANDLE, {
    fetchPolicy: 'network-only',
    variables:   { input: { handle: 'dashboard-banner' } },
    onCompleted: ( { settingsByHandle }: { settingsByHandle: Settings | null } ) => {
      if ( !settingsByHandle ) {
        dispatchMessage( 'error', 'Settings not found' )
        navigate( '/settings' )
        return
      }
      setSettings( settingsByHandle )
    },
    onError:     ( e: any ) => {
      console.error( e )
      dispatchMessage( 'error', 'Error occurred while fetching settings.' )
      navigate( '/settings' )
    }
  } )

  const handleSetFile = ( field: BannerImageField, file: any ): void => {
    // max 10MB
    if ( file.size < 10485760 ) {
      setFilesToUpload( prev => ( { ...prev, [ field ]: file } ) )
    } else {
      dispatchMessage( 'error', 'File is too large. Please upload a file with less than 10MB.' )
    }
  }

  const removeFileToUpload = ( field: BannerImageField ): void => {
    setFilesToUpload( prev => {
      const next = { ...prev }
      delete next[ field ]
      return next
    } )
  }

  useEffect( () => {
    if ( !settings ) return
    setFormData( {
                   name:   settings.name || '',
                   handle: settings.handle || '',
                   config: {
                     title:           settings.config?.title || '',
                     description:     settings.config?.description || '',
                     ctaTitle:        settings.config?.ctaTitle || '',
                     link:            settings.config?.link || '',
                     imageUrl:        settings.config?.imageUrl || '',
                     imageWideDesktopUrl: settings.config?.imageWideDesktopUrl || '',
                     imageDesktopUrl: settings.config?.imageDesktopUrl || '',
                     imageTabletUrl:  settings.config?.imageTabletUrl || '',
                     imageMobileUrl:  settings.config?.imageMobileUrl || ''
                   }
                 } )
    setFilesToUpload( {} )
    setErrors( {} )
  }, [ settings ] )

  const [ updateSettings, { loading: updating } ] = useMutation( UPDATE_SETTINGS, {
    onCompleted: () => {
      dispatchMessage( 'success', 'Settings updated!' )
      refetch()
    },
    onError:     () => dispatchMessage( 'error', 'Update failed. Please try again' )
  } )

  const [ createSettingsAttachment ] = useMutation( CREATE_SETTINGS_ATTACHMENT, {
    onCompleted: () => dispatchMessage( 'success', 'Attachment uploaded!' ),
    onError:     () => dispatchMessage( 'error', 'Attachment NOT uploaded!' )
  } )

  const [ deleteSettingsAttachmentByUrl, { loading: deleting } ] = useMutation(
    DELETE_SETTINGS_ATTACHMENT_BY_URL,
    {
      onCompleted: ( data: any ) => {
        const deleteResult = data?.deleteSettingsAttachmentByUrl

        if ( deleteResult?.__typename === 'FieldError' ) {
          dispatchMessage( 'error', 'Attachment not deleted. Server error.' )
        } else {
          dispatchMessage( 'success', 'Attachment deleted successfully' )
        }
      },
      onError:     () => dispatchMessage( 'error', 'Attachment not deleted. Please try again' )
    }
  )

  const uploadAttachment = async ( field: BannerImageField, fileToUpload: any ): Promise<any> => {
    const existingImageUrl = formData.config[ field ]

    try {
      if ( existingImageUrl ) {
        const deleted: any = await deleteSettingsAttachmentByUrl( { variables: { input: { url: existingImageUrl } } } )
        const deleteResult = deleted.data?.deleteSettingsAttachmentByUrl

        if ( deleteResult?.__typename === 'FieldError' ) {
          console.log( 'deleting old image: ', deleted )
          return null
        }
      }
    } catch ( error ) {
      console.error( `Error deleting old banner file (${ existingImageUrl }):`, error )
      return null
    }

    try {
      const newFileUrl: string = await uploadFile( fileToUpload, ( currentTenant.uuid + '/banners/' + settings.id ), 'true' )

      if ( newFileUrl ) {
        const filename: any      = fileToUpload.name.replace( /\.[^/.]+$/, '' )
        const fileTypeArray: any = fileToUpload.type.split( '/' )

        await createSettingsAttachment( {
                                          variables: {
                                            input: {
                                              'settings_id': settings.id,
                                              'type':        fileTypeArray[ 0 ],
                                              'file_ext':    fileTypeArray[ fileTypeArray.length - 1 ],
                                              'file_type':   fileToUpload.type,
                                              'name':        filename.length ? filename : 'no_name',
                                              'url':         newFileUrl
                                            }
                                          }
                                        } )

        return newFileUrl
      } else {
        console.log( 'Error while uploading new image occurred' )
        return null
      }
    } catch ( error ) {
      console.error( `Error uploading file (${ fileToUpload.name }):`, error )
      return null
    }
  }

  const updateField = ( field: keyof FormData, value: any ) => {
    setFormData( prev => ( { ...prev, [ field ]: value } ) )
  }

  const updateConfigField = ( field: keyof FormData['config'], value: any ) => {
    setFormData( prev => ( { ...prev, config: { ...prev.config, [ field ]: value } } ) )
  }

  const checkErrors = (): boolean => {
    const e = {
      name: !formData.name.trim(),
      // handle: !formData.handle.trim(),
      'config.title': !formData.config?.title?.trim()
      // 'config.imageUrl': !formData.config?.imageUrl?.trim()
      // 'config.description': !formData.config?.description?.trim(),
      // 'config.link':
      //   formData.config?.link?.trim()
      //   ? !/^https?:\/\/.+/i.test( formData.config.link.trim() )
      //   : false
    }
    setErrors( e )
    return Object.values( e ).some( Boolean )
  }

  const handleSubmit = async () => {
    if ( !settings ) return
    if ( checkErrors() ) return

    try {
      setUploading( true )
      const nextConfig = { ...formData.config }

      for ( const [ field, file ] of Object.entries( filesToUpload ) as [ BannerImageField, any ][] ) {
        const uploadedUrl = await uploadAttachment( field, file )

        if ( uploadedUrl ) {
          nextConfig[ field ] = uploadedUrl
        } else {
          dispatchMessage( 'error', 'Update settings failed. Image error.' )
          setUploading( false )
          return false
        }
      }

      await updateSettings( {
                              variables: {
                                input: {
                                  id:   settings.id,
                                  name: formData.name,
                                  // handle: formData.handle,
                                  config: nextConfig
                                }
                              }
                            } )
      setFilesToUpload( {} )
      setUploading( false )
    } catch ( e ) {
      setUploading( false )
      console.error( 'update error:', e )
    }
  }

  const busy = loading || updating || deleting || uploading

  // const configPreview = useMemo( () => JSON.stringify( formData.config, null, 2 ), [ formData.config ] )

  return (
    <MainLayout>
      <div className="max-w-screen-3xl mx-auto w-full">
        <div className="flex-grow lg:p-8 p-3 space-y-4 lg:space-y-8">
          { !currentUser && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4 mt-4 w-full" /> }

          { currentUser?.admin ? (
            <>
              <div className="w-24 my-4">
                <Button
                  label="Back"
                  blue
                  onClick={ () => navigate( -1 ) }
                  icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
                />
              </div>

              { busy && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4" /> }

              { !busy && (
                <div className="flex flex-col items-center w-full bg-white rounded-3xl p-4 xl:p-5">
                  <div className="flex flex-wrap items-center w-full mb-3 pt-2">
                    <div className="w-full lg:w-1/3">Settings Name</div>
                    <div className="w-full lg:w-2/3">
                      <TextField
                        label="Settings Name"
                        value={ formData.name }
                        name="name"
                        type="text"
                        onChange={ ( val: string ) => updateField( 'name', val ) }
                        errorField={ errors.name ? 'name' : '' }
                        isRequired
                      />
                    </div>
                  </div>

                  {/*<div className="flex flex-wrap items-center w-full mb-3 pt-2">*/ }
                  {/*  <div className="w-full lg:w-1/3">*/ }
                  {/*    Handle*/ }
                  {/*    <p className="text-xs text-gray-500">Must be unique</p>*/ }
                  {/*  </div>*/ }
                  {/*  <div className="w-full lg:w-2/3">*/ }
                  {/*    <TextField*/ }
                  {/*      label="Handle"*/ }
                  {/*      value={formData.handle}*/ }
                  {/*      name="handle"*/ }
                  {/*      type="text"*/ }
                  {/*      onChange={(val: string) => updateField('handle', val)}*/ }
                  {/*      errorField={errors.handle ? 'handle' : ''}*/ }
                  {/*      isRequired*/ }
                  {/*    />*/ }
                  {/*  </div>*/ }
                  {/*</div>*/ }

                  {/* CONFIG FIELDS */ }
                  <div className="flex flex-wrap items-center w-full mb-6">
                    Settings Config:
                  </div>

                  <div className="flex flex-wrap items-center w-full mb-3 pt-2">
                    <div className="w-full lg:w-1/3">Title</div>
                    <div className="w-full lg:w-2/3">
                      <TextField
                        label="Title"
                        value={ formData.config.title || '' }
                        name="config.title"
                        type="text"
                        onChange={ ( val: string ) => updateConfigField( 'title', val ) }
                        errorField={ errors[ 'config.title' ] ? 'config.title' : '' }
                        isRequired
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center w-full mb-3 pt-2">
                    <div className="w-full lg:w-1/3">Description</div>
                    <div className="w-full lg:w-2/3">
                      <TextField
                        label="Description"
                        value={ formData.config.description || '' }
                        name="config.description"
                        type="text"
                        onChange={ ( val: string ) => updateConfigField( 'description', val ) }
                        errorField={ errors[ 'config.description' ] ? 'config.description' : '' }
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center w-full mb-3 pt-2">
                    <div className="w-full lg:w-1/3">Button Title</div>
                    <div className="w-full lg:w-2/3">
                      <TextField
                        label="Button Title"
                        value={ formData.config.ctaTitle || '' }
                        name="config.ctaTitle"
                        type="text"
                        onChange={ ( val: string ) => updateConfigField( 'ctaTitle', val ) }
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center w-full mb-3 pt-2">
                    <div className="w-full lg:w-1/3">Button Link</div>
                    <div className="w-full lg:w-2/3">
                      <TextField
                        label="Button Link"
                        value={ formData.config.link || '' }
                        name="config.link"
                        type="text"
                        onChange={ ( val: string ) => updateConfigField( 'link', val ) }
                        errorField={ errors[ 'config.link' ] ? 'config.link' : '' }
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center w-full mb-6">
                    <div className="font-medium text-blue-header w-full mb-3">Banner Images</div>

                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4">
                      { bannerUploads.map( upload => {
                        const fileToUpload = filesToUpload[ upload.field ]
                        const currentImageUrl = formData.config[ upload.field ] || formData.config.imageUrl

                        return (
                          <div key={ upload.field } className="w-full">
                            <div className="mb-2">
                              <div className="font-medium text-blue-input">{ upload.title }</div>
                              <p className="text-xs text-gray-500">{ upload.size }</p>
                            </div>

                            <div className="cursor-pointer no-select p-6 rounded-2xl bg-grey-light-secondary">
                              <Dropzone onDrop={ ( attachedFile: any ): any => handleSetFile( upload.field, attachedFile[ 0 ] ) } multiple={ false }>
                                { ( { getRootProps, getInputProps }: any ): any => (
                                  <section>
                                    <div { ...getRootProps() }>
                                      <input { ...getInputProps() } accept="image/*" />

                                      <div className="flex items-center justify-center flex-col space-y-2">
                                        <UploadCloud className="w-12 h-12" />
                                        <p className="text-blue-input text-sm font-medium">Drag 'n' drop or click to select file</p>
                                      </div>
                                    </div>
                                  </section>
                                ) }
                              </Dropzone>
                            </div>

                            { fileToUpload &&
                              <div className="mt-4 pb-2 flex items-center flex-wrap gap-3">
                                <FilePreview
                                  file={ fileToUpload }
                                  onRemove={ (): void => removeFileToUpload( upload.field ) }
                                />
                              </div>
                            }

                            { currentImageUrl &&
                              <div className="mt-4">
                                <div className="font-medium text-blue-input w-full mb-2">Current image</div>
                                <div className="flex items-center justify-center w-full aspect-[16/6] bg-grey-light-secondary rounded-xl overflow-hidden">
                                  <Image
                                    klass="w-full h-full object-cover"
                                    src={ appCdnUrl + currentImageUrl }
                                    alt={ upload.title }
                                    defaultSrc={ assetFilePath( 'png' ) }
                                  />
                                </div>
                              </div>
                            }
                          </div>
                        )
                      } ) }
                    </div>
                  </div>

                  {/*<pre className="w-full bg-gray-50 rounded-xl p-3 mt-4 text-xs overflow-auto">*/ }
                  {/*  { configPreview }*/ }
                  {/*</pre>*/ }

                  <div className="flex flex-wrap w-full">
                    <div className="flex pt-5">
                      <Button label="Submit" yellow onClick={ handleSubmit } />
                    </div>
                  </div>
                </div>
              ) }
            </>
          ) : (
              <div className="row flex flex-col items-center w-full bg-white rounded-3xl p-3 xl:p-4">
                Access Denied!
              </div>
            ) }
        </div>
      </div>
    </MainLayout>
  )
}
